import type { CommonResult } from '@zoryn/network'
import type { HttpRequest } from '@zoryn/network'
import type { TaskActionLog, TaskSummaryStats } from '../types'
import { DEFAULT_TASK_API_PREFIX } from '../constants'

export interface TaskDto {
  id: number | string
  title: string
  description?: string
  status: string
  priority: string
  module: string
  bizType: string
  bizId?: string
  bizRoute?: string
  bizPayload?: Record<string, unknown>
  assigneeId?: number
  assigneeName?: string
  creatorId?: number
  creatorName?: string
  dueTime?: string
  createTime?: string
  finishTime?: string
}

export interface TaskLogDto {
  time?: string
  operator?: string
  action?: string
  remark?: string
}

type PageLikeResult<T> = {
  data?: T
  total?: number
  success?: boolean
}

export function createTaskApi(
  http: HttpRequest,
  apiPrefix = DEFAULT_TASK_API_PREFIX
) {
  async function fetchMyTasks() {
    const body = await http.get<PageLikeResult<TaskDto[]>>({
      url: `${apiPrefix}/myTasks`,
    })
    return Array.isArray(body.data) ? body.data : []
  }

  async function fetchTaskSummary() {
    const body = await http.get<CommonResult<TaskSummaryStats>>({
      url: `${apiPrefix}/summary`,
    })
    return body.data ?? { pending: 0, processing: 0, overdue: 0, doneToday: 0 }
  }

  async function fetchTaskLogs(taskId: string) {
    const body = await http.get<CommonResult<TaskLogDto[]>>({
      url: `${apiPrefix}/logs`,
      params: { taskId },
    })
    return body.data ?? []
  }

  async function startTaskApi(taskId: string) {
    await http.post({
      url: `${apiPrefix}/start`,
      params: { id: Number(taskId) },
    })
  }

  async function completeTaskApi(taskId: string) {
    await http.post({
      url: `${apiPrefix}/complete`,
      params: { id: Number(taskId) },
    })
  }

  async function cancelTaskApi(taskId: string) {
    await http.post({
      url: `${apiPrefix}/cancel`,
      params: { id: Number(taskId) },
    })
  }

  return {
    fetchMyTasks,
    fetchTaskSummary,
    fetchTaskLogs,
    startTaskApi,
    completeTaskApi,
    cancelTaskApi,
  }
}

export function mapTaskLogs(dtos: TaskLogDto[]): TaskActionLog[] {
  return dtos.map((item) => ({
    time: item.time ?? '',
    operator: item.operator ?? '',
    action: item.action ?? '',
    remark: item.remark,
  }))
}
